import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/api";

// ─── Types ────────────────────────────────────────────────────────────────────

interface PersonData {
  name: string;
  dob: string;
  role: string;
}

type ContextType = "business_partner" | "creative_collab" | "hire" | "general";

const emptyPerson = (): PersonData => ({ name: "", dob: "", role: "" });

// ─── Shared UI atoms ──────────────────────────────────────────────────────────

const CardOption: React.FC<{
  label: string;
  desc?: string;
  selected: boolean;
  onClick: () => void;
}> = ({ label, desc, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="p-3 rounded-2xl border text-left transition-all duration-200"
    style={
      selected
        ? {
            background: "#F7F2FB",
            borderColor: "#C084FC",
            boxShadow: "0 1px 4px #C084FC22",
          }
        : { background: "white", borderColor: "#E5E7EB" }
    }
  >
    <p
      className="text-xs font-semibold"
      style={{ color: selected ? "#6D4C92" : "#5D544D" }}
    >
      {label}
    </p>
    {desc && (
      <p className="text-[10px] mt-0.5" style={{ color: "#9D8FA8" }}>
        {desc}
      </p>
    )}
  </button>
);

const InputField: React.FC<{
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
}> = ({ label, type = "text", value, onChange, placeholder, required }) => (
  <div>
    <label
      className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-1.5"
      style={{ color: "#6A548A" }}
    >
      {label}
      {required && (
        <span className="ml-1" style={{ color: "#C084FC" }}>
          *
        </span>
      )}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-xl px-4 py-2.5 text-sm transition-all focus:outline-none"
      style={{
        background: "white",
        border: "1px solid #E5E7EB",
        color: "#3D2B5A",
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = "#C084FC";
        e.currentTarget.style.boxShadow = "0 0 0 2px #C084FC18";
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = "#E5E7EB";
        e.currentTarget.style.boxShadow = "none";
      }}
    />
  </div>
);

const PersonBlock: React.FC<{
  label: string;
  idx: number;
  data: PersonData;
  onChange: (field: keyof PersonData, value: string) => void;
}> = ({ label, idx, data, onChange }) => (
  <div>
    <div className="flex items-center gap-2 mb-4">
      <span
        className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
        style={{ background: "linear-gradient(135deg,#C084FC,#6D4C92)" }}
      >
        {idx === 0 ? "1" : "2"}
      </span>
      <h3
        className="text-xs font-semibold tracking-[0.15em] uppercase"
        style={{ color: "#6A548A" }}
      >
        {label}
      </h3>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
      <InputField
        label="ชื่อ"
        value={data.name}
        onChange={(v) => onChange("name", v)}
        placeholder="กรอกชื่อ"
      />
      <InputField
        label="วันเกิด"
        type="date"
        value={data.dob}
        onChange={(v) => onChange("dob", v)}
        required
      />
      <InputField
        label="บทบาท"
        value={data.role}
        onChange={(v) => onChange("role", v)}
        placeholder="เช่น ผู้ก่อตั้ง, นักลงทุน"
      />
    </div>
  </div>
);

// ─── Loading Screen ───────────────────────────────────────────────────────────

const LOADING_STEPS = [
  { label: "Reading day-planet rulers...", icon: "☉" },
  { label: "Cross-checking planetary chemistry...", icon: "◈" },
  { label: "กำลังให้คะแนนความไว้วางใจและการเสริมกันด้านความมั่งคั่ง...", icon: "◉" },
  { label: "กำลังสแกนสัญญาณเตือน...", icon: "⚠" },
  { label: "กำลังล็อกช่วงเวลาของวันนี้...", icon: "✧" },
];

const LoadingScreen: React.FC = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setActiveIdx((prev) => {
        const next = prev + 1;
        if (next >= LOADING_STEPS.length) {
          clearInterval(timerRef.current!);
          return prev;
        }
        return next;
      });
    }, 420);
    return () => clearInterval(timerRef.current!);
  }, []);

  const currentStep = LOADING_STEPS[activeIdx];

  return (
    <div className="min-h-screen w-full bg-white flex flex-col items-center justify-center px-6 py-16 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-24 -left-24 w-72 h-72 rounded-full blur-3xl"
          style={{
            background: "radial-gradient(circle, #C084FC 0%, transparent 70%)",
            opacity: 0.07,
          }}
        />
        <div
          className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full blur-3xl"
          style={{
            background: "radial-gradient(circle, #C084FC 0%, transparent 70%)",
            opacity: 0.1,
          }}
        />
      </div>

      <div className="relative mb-10">
        <svg
          width="112"
          height="112"
          viewBox="0 0 112 112"
          className="animate-spin"
          style={{ animationDuration: "6s" }}
        >
          <circle
            cx="56"
            cy="56"
            r="48"
            fill="none"
            stroke="#C084FC"
            strokeWidth="1"
            strokeDasharray="5 7"
            opacity="0.35"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C084FC]/20 to-[#6D4C92]/20 border border-[#C084FC]/30 flex items-center justify-center">
            <span className="text-xl text-[#6D4C92]">🛡</span>
          </div>
        </div>
      </div>

      <motion.h2
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-xl sm:text-2xl font-light tracking-wide mb-1 text-center"
        style={{ color: "#6A548A" }}
      >
        กำลังเรียกใช้การสแกน Check-Mate
      </motion.h2>
      <p
        className="text-xs tracking-[0.18em] uppercase mb-10 text-center"
        style={{ color: "#9D8FA8" }}
      >
        Trust · Wealth · Red Flags
      </p>

      <div className="w-full max-w-xs min-h-[52px] flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeIdx}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-[#F7F2FB] border border-[#C084FC]/30 shadow-sm"
          >
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-[#C084FC] to-[#6D4C92]">
              <span className="text-white text-sm">{currentStep.icon}</span>
            </div>
            <span className="text-sm font-medium" style={{ color: "#6A548A" }}>
              {currentStep.label}
            </span>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex gap-2 mt-6">
        {LOADING_STEPS.map((_, i) => (
          <div
            key={i}
            className="rounded-full transition-all duration-500"
            style={{
              width: i === activeIdx ? 20 : 6,
              height: 6,
              background: i <= activeIdx ? "#C084FC" : "#E5E7EB",
            }}
          />
        ))}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const CONTEXT_OPTIONS: { val: ContextType; label: string; desc: string }[] = [
  {
    val: "business_partner",
    label: "หุ้นส่วนธุรกิจ",
    desc: "Co-founding / joint venture",
  },
  { val: "hire", label: "Hire", desc: "Employer ↔ candidate" },
  {
    val: "creative_collab",
    label: "ความร่วมมือเชิงสร้างสรรค์",
    desc: "โปรเจกต์ / ความร่วมมือเชิงสร้างสรรค์",
  },
  { val: "general", label: "General", desc: "ยังไม่แน่ใจ" },
];

const CheckMateForm: React.FC = () => {
  const navigate = useNavigate();
  const [person1, setPerson1] = useState<PersonData>(emptyPerson());
  const [person2, setPerson2] = useState<PersonData>(emptyPerson());
  const [context, setContext] = useState<ContextType>("general");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updatePerson = (
    which: "person1" | "person2",
    field: keyof PersonData,
    value: string,
  ) => {
    const setter = which === "person1" ? setPerson1 : setPerson2;
    setter((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!person1.dob || !person2.dob) {
      setError("ต้องกรอกวันเกิดของทั้งสองคน");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        person1,
        person2,
        context,
        userId: localStorage.getItem("userId") || undefined,
      };
      const response = await api.post("/backend/checkmate/scan", payload);
      if (response.data?.success) {
        sessionStorage.setItem("checkMateScan", JSON.stringify(response.data));
        navigate("/checkmate/result");
      } else {
        setLoading(false);
        const errs: string[] = response.data?.errors;
        setError(
          Array.isArray(errs) && errs.length > 0
            ? errs.join(" · ")
            : response.data?.message || "เรียกใช้การสแกนไม่สำเร็จ กรุณาลองอีกครั้ง",
        );
      }
    } catch (err: any) {
      setLoading(false);
      const errs: string[] = err?.response?.data?.errors;
      setError(
        Array.isArray(errs) && errs.length > 0
          ? errs.join(" · ")
          : err?.response?.data?.message ||
              "เกิดข้อผิดพลาด กรุณาลองอีกครั้ง",
      );
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="min-h-screen w-full bg-white">
      <div className="h-1 w-full" />

      <div className="min-h-screen w-full px-4 sm:px-6 md:px-10 lg:px-16 py-8 sm:py-12">
        <div className="w-full max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="inline-flex items-center gap-2 mb-3">
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: "#C084FC" }}
              />
              <span
                className="text-[10px] tracking-[0.25em] uppercase"
                style={{ color: "#9D8FA8" }}
              >
                เครื่องมือซินาสตรีธุรกิจ
              </span>
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: "#C084FC" }}
              />
            </div>
            <h1
              className="text-xl sm:text-2xl md:text-3xl font-light tracking-wide"
              style={{ color: "#6A548A" }}
            >
              สแกน Check-Mate
            </h1>
            <p className="text-xs sm:text-sm mt-2" style={{ color: "#9D8FA8" }}>
              Trust Score · Wealth Synergy · Red Flags
            </p>
          </div>

          {/* Card */}
          <div
            className="rounded-3xl p-5 sm:p-6 md:p-8 shadow-sm border"
            style={{ background: "white", borderColor: "#EEF2FF" }}
          >
            <div className="space-y-6 sm:space-y-8">
              <PersonBlock
                label="บุคคลที่ 1"
                idx={0}
                data={person1}
                onChange={(field, value) =>
                  updatePerson("person1", field, value)
                }
              />
              <PersonBlock
                label="บุคคลที่ 2"
                idx={1}
                data={person2}
                onChange={(field, value) =>
                  updatePerson("person2", field, value)
                }
              />

              <div>
                <label
                  className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-2"
                  style={{ color: "#6A548A" }}
                >
                  บริบทความสัมพันธ์
                </label>
                <div className="grid grid-cols-1 xs:grid-cols-2 gap-2">
                  {CONTEXT_OPTIONS.map((opt) => (
                    <CardOption
                      key={opt.val}
                      label={opt.label}
                      desc={opt.desc}
                      selected={context === opt.val}
                      onClick={() => setContext(opt.val)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Error */}
          <AnimatePresence>
            {error && (
              <motion.p
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mt-3 text-red-500 text-xs text-center font-medium"
              >
                {error}
              </motion.p>
            )}
          </AnimatePresence>

          {/* Submit */}
          <div className="mt-6">
            <button
              onClick={handleSubmit}
              className="w-full rounded-full py-3 px-8 text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:opacity-90"
              style={{
                background: "linear-gradient(to right, #C084FC, #7DD3FC)",
              }}
            >
              🛡 Run Check-Mate Scan
            </button>
          </div>

          <div
            className="text-center mt-6 text-[10px] sm:text-[11px] tracking-wide"
            style={{ color: "#9D8FA8" }}
          >
            <span
              className="inline-block border-t w-8 sm:w-12 mx-2 align-middle"
              style={{ borderColor: "#C084FC40" }}
            />
            not a judgment of character — only planetary chemistry
            <span
              className="inline-block border-t w-8 sm:w-12 mx-2 align-middle"
              style={{ borderColor: "#C084FC40" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckMateForm;
